import React, { useRef, Suspense } from 'react';
import styled from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';

import {
  DataStore,
  Environment,
  FontFamilies,
  FontSizes,
  Logger,
  Theme,
  isValidProviderResult,
  Colors,
  MediaQueries,
} from 'common';

import {
  Button,
  Modal,
  Loader,
  NumericSelect,
  Stepper,
  Text,
  TextInput,
  PSmall,
} from 'common/components';

import {
  withCommon
} from 'common/hocs';

import {
  lazyLoadComponent
} from 'common/utils';

import {
  EventName,
  FullWidthContainer,
  StepperContainer,
} from 'ui/event/styled';

import {
  getCheckoutSteps
} from 'ui/event/eventUtils';

import {
  EventProvider,
  OrderProvider,
} from 'providers';

import {
  arePackageVariantsSelected,
  applyAssignmentsToSeats,
  buildPackageSlots,
  buildPackagesCheckoutPayload,
  filterVariantsForCount,
  findSeatKey,
  getPackageProductItems,
  getPackageTicketItems,
  loadPackageAssignments,
  packageItemId,
  savePackageAssignments,
  slotsAboveCount,
  variantsStateFromPackages,
  writeSelectedPackages,
} from 'ui/event/seatingPackages';

// Dynamically import SeatSelector to reduce bundle size
const SeatSelector = lazyLoadComponent('SeatSelector');

/** sessionStorage-backed flag: URL can be stale when the page is restored from bfcache */
const SEATING_SESSION_REVALIDATE_KEY = 'seatingSessionRevalidate';

const CenterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${props => props.padding || '20px'};
`;

const SeatMapOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  height: 100vh;
  width: 100%;
  padding: 24px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const SeatMapErrorReloadContainer = styled.div`
  background-color: ${Theme.Card.Background};
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  text-align: center;
  max-width: 400px;

  @media only screen and ${MediaQueries.Md} {
    padding: 20px;
  }
`;

const ModalTitle = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Big};
  margin-bottom: 15px;
`;

const ModalMessage = styled(Text)`
  font-size: ${FontSizes.Small};
  margin-bottom: 20px;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
`;

const TicketSummaryContainer = styled.div`
  width: 95%;
  max-width: 650px;
  margin: 20px auto;
  margin-bottom: ${props => props.noMarginBottom ? '0' : '60px'};
  padding: 20px;
  background-color: ${Theme.Card.Background};
  border: 1px solid ${Theme.Card.Border};
  border-radius: ${Theme.Card.BorderRadius};
  box-shadow: ${Theme.Card.BoxShadow};
  display: flex;
  flex-direction: column;

  @media only screen and ${MediaQueries.Sm} {
    width: 100%;
  }
`;

const TicketSummaryTitle = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Medium};
  margin-bottom: 15px;
  color: ${Theme.Text.Color};
`;

const EmptySummaryText = styled(Text)`
  font-size: ${FontSizes.Small};
  color: ${Theme.Text.Color};
  font-style: italic;
  text-align: center;
  padding: 20px 0;
`;

const TicketSummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
`;

const TicketSummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px 0 12px;
  margin-top: 8px;
  border-top: 1px solid ${Theme.Divider.Color || '#e0e0e0'};
  background-color: ${Theme.Card.Background || '#ffffff'};
`;

const TicketSummaryTotalLabel = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Medium};
  color: ${Theme.Text.Color};
`;

const TicketSummaryTotalAmount = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Big};
  color: ${Theme.Text.Color};
`;

const TicketSummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.padding || '12px'};
  background-color: #f6f8fa;
  border-radius: 6px;
  border: 1px solid ${Theme.Card.Border};
  gap: 12px;
`;

const TicketSummaryRemoveButton = styled.button`
  background: none !important;
  border: none !important;
  color: #e74c3c !important;
  cursor: pointer !important;
  font-size: 18px !important;
  font-weight: bold !important;
  font-family: ${FontFamilies.Regular} !important;
  padding: 4px 0 !important;
  border-radius: 4px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 32px !important;
  height: 32px !important;
  transition: background-color 0.2s !important;
  flex-shrink: 0 !important;
`;

const PackageRemoveButton = styled.button`
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  width: 36px !important;
  height: 36px !important;
  padding: 0 !important;
  border-radius: 10px !important;
  background: #ffffff !important;
  border: 1px solid #e74c3c !important;
  color: #e74c3c !important;
  cursor: pointer !important;
`;

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 7h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const TicketSummaryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TicketSummarySeatId = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Small};
  color: ${props => props.color || Theme.Text.Color};
`;

const TicketSummaryType = styled(Text)`
  font-size: ${FontSizes.Smaller};
  color: ${Theme.Text.Color};
  opacity: 0.8;
`;

const TicketSummaryPrice = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Small};
  color: ${Colors.PrimaryOne || '#3498db'};
  white-space: nowrap;
`;

const PackageCopyHeader = styled(TicketSummaryItem)`
  background: none;
  border: none;
  padding: 0;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;

  @media only screen and ${MediaQueries.Md} {
    flex-direction: row;
    align-items: center;
  }
`;

const PackageCopyInfo = styled(TicketSummaryInfo)`
  min-width: 0;
  flex: 1;
`;

const PackageCopyTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const PackageCopyActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;

  @media only screen and ${MediaQueries.Md} {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: auto;
    flex-shrink: 0;
    gap: 8px;
  }
`;

const PackageSeatButtonWrap = styled.div`
  width: 100%;

  > div {
    width: 100% !important;
  }

  @media only screen and ${MediaQueries.Md} {
    width: 140px;

    > div {
      width: 140px !important;
    }
  }
`;

const PackageSeatGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid ${Theme.Card.Border};
  border-radius: 8px;
  background-color: ${Theme.Card.Background || '#ffffff'};
`;

const PackageSeatList = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 4px;
  padding: 10px 10px 10px 12px;
  border-left: 3px solid ${Colors.PrimaryOne};
  background-color: #f6f8fa;
  border-radius: 0 6px 6px 0;
`;

const TicketSummaryButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid ${Theme.Card.Border};

  @media only screen and ${MediaQueries.Md} {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    
    button {
      width: auto !important;
      min-width: 150px;
    }
  }
`;

const CouponSection = styled.div`
  margin-top: 10px;
  padding-top: 16px;
  border-top: 1px solid ${Theme.Divider.Color || Theme.Card.Border};
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${Theme.Divider.Color || Theme.Card.Border};
  margin: 8px 0 16px 0;
`;

const CouponSectionHeader = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Medium};
  margin-bottom: 8px;
  color: ${Theme.Text.Color};
`;

const CouponRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const CouponInputWrap = styled.div`
  flex: 1;
`;

const OfferBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: ${props => props.$first ? '0' : '16px'};
  margin-top: ${props => props.$first ? '0' : '16px'};
  border-top: ${props => props.$first ? 'none' : `1px solid ${Theme.Divider.Color || Theme.Card.Border}`};
`;

const OfferPriceRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const OfferShowMore = styled.button`
  background: none !important;
  border: none !important;
  padding: 0 !important;
  color: ${Colors.PrimaryOne} !important;
  font-family: ${FontFamilies.Bold} !important;
  font-size: ${FontSizes.Small} !important;
  cursor: pointer !important;
  text-align: left !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  width: 110px !important;
`;

const IncludedItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VariantButton = styled.button`
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid ${props => props.$selected ? Colors.PrimaryOne : Colors.Grey};
  background-color: ${props => props.$selected ? Colors.PrimaryOne : Colors.White};
  color: ${props => props.$selected ? Colors.White : Colors.Black};
  font-weight: ${props => props.$selected ? 'bold' : 'normal'};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VariantButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const EventSeatingUI = props => {

  const [state, setState] = React.useState({
    event: DataStore.get('selectedEvent') || {},
    seating: null,
    loading: true,
  });

  const [expiryTime, setExpiryTime] = React.useState(null);
  const [orderId, setOrderId] = React.useState(null);
  const [remainingTime, setRemainingTime] = React.useState(null);
  const [showTimeWarningModal, setShowTimeWarningModal] = React.useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = React.useState(false);
  const [pendingRemoval, setPendingRemoval] = React.useState(null);
  const [showRemovePackageModal, setShowRemovePackageModal] = React.useState(false);
  const [pendingPackageRemoval, setPendingPackageRemoval] = React.useState(null);
  const [ticketTypes, setTicketTypes] = React.useState([]);
  const [selectedSeatsFromSelector, setSelectedSeatsFromSelector] = React.useState(new Map()); // Map of seatId -> { ticketTypeId, ticketTypeTitle, blockId }
  const [isSeatSelectorOpen, setIsSeatSelectorOpen] = React.useState(false);
  const [activePackageSeat, setActivePackageSeat] = React.useState(null);
  const [isLoadingSeatingData, setIsLoadingSeatingData] = React.useState(false);
  const [seatingLoadFailed, setSeatingLoadFailed] = React.useState(false);
  const [pricesLoadFailed, setPricesLoadFailed] = React.useState(false);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = React.useState(false);
  const [couponCode, setCouponCode] = React.useState('');
  const [couponError, setCouponError] = React.useState('');
  const [appliedCouponCode, setAppliedCouponCode] = React.useState(null);
  const [appliedCouponItems, setAppliedCouponItems] = React.useState([]);
  const [isApplyingCoupon, setIsApplyingCoupon] = React.useState(false);
  const [eventPackages, setEventPackages] = React.useState([]);
  const [selectedPackages, setSelectedPackages] = React.useState({});
  const [packageVariants, setPackageVariants] = React.useState({});
  const [packageAssignments, setPackageAssignments] = React.useState(loadPackageAssignments);
  const [packageCounterEpoch, setPackageCounterEpoch] = React.useState(0);
  const [expandedOfferIds, setExpandedOfferIds] = React.useState({});
  const checkIntervalRef = useRef(null);
  const timeWarningShownRef = useRef(false);
  const packageAssignmentsRef = useRef(loadPackageAssignments());
  const packageUpdateLock = useRef(false);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const applyCouponToSelectedTickets = React.useCallback((selectedSeats, appliedItems) => {
    const newMap = new Map(selectedSeats);

    for (const item of appliedItems || []) {

      const selectedOfTicketType = Array.from(newMap.values())
        .filter(seat => seat.ticketTypeId === item.event_price_id && !seat.slotId);

      for (let i = 0; i < item.quantity_limit; i++) {

        if (selectedOfTicketType.length > i) {

          const ticketType = ticketTypes.find(tt => tt.ticket_type_id === selectedOfTicketType[i].ticketTypeId);
          selectedOfTicketType[i].coupon_applied = true;
          selectedOfTicketType[i].coupon_applied_price = (ticketType.price - (item.fixed_discount || 0)) * (item.percent_discount || 1);
        }
      }
    }

    return newMap;
  }, [ticketTypes]);

  const clearCouponFromSelectedTickets = (selectedSeatsFromSelector) => { 
    const newMap = new Map(selectedSeatsFromSelector);
    for (const seat of newMap.values()) {
      seat.coupon_applied = false;
      delete seat.coupon_applied_price;
    }
    setSelectedSeatsFromSelector(newMap);
  };

  const onApplyCoupon = React.useCallback(async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    if (!state.event.id) {
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const result = await OrderProvider.getCoupon(couponCode.trim(), state.event.id);

      if (!isValidProviderResult(result)) {
        setCouponError('Unable to validate coupon. Please try again.');
        Logger.error('EventSeating', 'onApplyCoupon', result.message);
        setAppliedCouponCode(null);
        setAppliedCouponItems([]);
        return;
      }

      if (!result.data.valid) {
        setCouponError(result.data.message || 'Invalid coupon code');
        setAppliedCouponCode(null);
        setAppliedCouponItems([]);
        return;
      }

      if (!Array.isArray(result.data.items) || result.data.items.length === 0) {
        setCouponError('Invalid coupon code');
        setAppliedCouponCode(null);
        setAppliedCouponItems([]);
        return;
      }

      const code = result.data.coupon_code || couponCode.trim();
      setAppliedCouponCode(code);
      setAppliedCouponItems(result.data.items);
      setSelectedSeatsFromSelector(applyCouponToSelectedTickets(selectedSeatsFromSelector, result.data.items));

      // Update URL with new orderId, replacing current entry to prevent back button issues
      const newSearchParams = new URLSearchParams(props.common.location.search);
      newSearchParams.set('revalidate', 'true');
      DataStore.set(SEATING_SESSION_REVALIDATE_KEY, true);
      const newPath = `${props.common.location.pathname}?${newSearchParams.toString()}`;
      props.common.navigate(newPath, { replace: true });

      Logger.info('EventSeating', 'onApplyCoupon', `Coupon applied: ${code}`);
    } catch (error) {
      Logger.error('EventSeating', 'onApplyCoupon', error);
      setCouponError('Unable to validate coupon. Please try again.');
      setAppliedCouponCode(null);
    } finally {
      setIsApplyingCoupon(false);
    }
  }, [couponCode, state.event.id, selectedSeatsFromSelector, applyCouponToSelectedTickets, props.common]);

  const onRemoveCoupon = React.useCallback(() => {
    setCouponCode('');
    setAppliedCouponCode(null);
    setCouponError('');
    setAppliedCouponItems([]);
    DataStore.clear(SEATING_SESSION_REVALIDATE_KEY);
    clearCouponFromSelectedTickets(selectedSeatsFromSelector);
  }, [selectedSeatsFromSelector]);

  React.useEffect(() => {
    const handlePageShow = (event) => {
      // Hide loading when page is shown (including from bfcache)
      Logger.info('EventSeating', 'handlePageShow', 'Page shown');
      
      const searchParams = new URLSearchParams(window.location.search);
      const revalidateFromUrl = searchParams.get('revalidate') === 'true';
      const revalidateFromStore = DataStore.get(SEATING_SESSION_REVALIDATE_KEY) === true;
      // bfcache restore can serve a stale URL; sessionStorage still has the flag from Apply
      const shouldRevalidate = event.persisted
        ? revalidateFromStore
        : (revalidateFromUrl || revalidateFromStore);

      if (shouldRevalidate) {
        Logger.info('EventSeating', 'handlePageShow', `Revalidate (bfcache: ${!!event.persisted}), refreshing page`);
        window.location.reload();
      } else {
        props.common.hideLoading();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []); // eslint-disable-line

  React.useEffect(() => {
    // Basic validation - can be expanded later
    if (!state.event?.id) {
      props.common.navigate('/');
      return;
    }

    // Get expiry and orderId from query params
    const searchParams = new URLSearchParams(props.common.location.search);
    const expiryTimeParam = searchParams.get('expiry');
    const orderIdParam = searchParams.get('orderId');

    if (!expiryTimeParam) {
      Logger.error('EventSeating', 'init', 'No expiry time provided');
      props.common.navigate(-1);
      return;
    }

    const expiryTimestamp = parseInt(expiryTimeParam);
    setExpiryTime(expiryTimestamp);

    const now = Date.now();
    const secondsRemaining = Math.max(0, Math.floor((expiryTimestamp - now) / 1000));
    setRemainingTime(formatTime(secondsRemaining));

    // If expired on load, navigate away
    if (secondsRemaining <= 0) {
      Logger.error('EventSeating', 'init', 'Seat selection session expired');
      props.common.navigate(`/payment-result/expired?order_id=${orderIdParam}`);
      return;
    }

    // Initialize order and load seats
    const initializeOrder = async () => {
      let currentOrderId = orderIdParam;
      const revalidateParam = searchParams.get('revalidate') === 'true';
      const revalidateFromStore = DataStore.get(SEATING_SESSION_REVALIDATE_KEY) === true;

      if (revalidateParam || revalidateFromStore) {
        DataStore.clear(SEATING_SESSION_REVALIDATE_KEY);
        currentOrderId = null;

        const newSearchParams = new URLSearchParams();
        newSearchParams.set('expiry', new Date(Date.now() + 10 * 60 * 1000).getTime());
        const newPath = `${props.common.location.pathname}?${newSearchParams.toString()}`;
        props.common.navigate(newPath, { replace: true });
        return;
      }

      // If no order ID, create an empty order
      if (!currentOrderId) {
        writeSelectedPackages({});
        setSelectedPackages({});
        setPackageVariants({});
        packageAssignmentsRef.current = {};
        setPackageAssignments({});
        savePackageAssignments({});

        props.common.showLoading('Initializing seat selection...');
        try {
          const emptyOrderResult = await OrderProvider.createEmptyOrder(state.event.id);
          if (isValidProviderResult(emptyOrderResult) && emptyOrderResult.data?.order_id) {
            currentOrderId = emptyOrderResult.data.order_id;
            setOrderId(currentOrderId);
            
            // Update URL with new orderId, replacing current entry to prevent back button issues
            const newSearchParams = new URLSearchParams(props.common.location.search);
            newSearchParams.set('orderId', currentOrderId);
            if (emptyOrderResult.data?.token) {
              newSearchParams.set('token', emptyOrderResult.data.token);
            }
            const newPath = `${props.common.location.pathname}?${newSearchParams.toString()}`;
            props.common.navigate(newPath, { replace: true });
          } else {
            Logger.error('EventSeating', 'init', emptyOrderResult?.message || 'Failed to create empty order');
            props.common.showToast('Unable to initialize order. Please try again.');
            return;
          }
        } catch (error) {
          Logger.error('EventSeating', 'init', error);
          props.common.showToast('Unable to initialize order. Please try again.');
          props.common.hideLoading();
          return;
        }
      } else {
        setOrderId(currentOrderId);
      }

      // If we have an order ID, fetch existing seats for this order
      if (currentOrderId) {
        try {
          const seatsResult = await OrderProvider.getOrderSeats(currentOrderId);
          if (isValidProviderResult(seatsResult) && Array.isArray(seatsResult.data?.seats)) {
            // Map the seats data to selectedSeatsFromSelector format
            const seatsMap = new Map();
            seatsResult.data.seats.forEach(seat => {
              if (seat.seat_id) {
                // Regular seat ticket
                seatsMap.set(seat.seat_id, {
                  ticketTypeId: seat.ticket_type_id,
                  ticketTypeTitle: seat.ticket_type_name || 'Ticket',
                  blockId: seat.block_id,
                  blockName: seat.block_name,
                  seatRow: seat.seat_row || '',
                  seatNumber: seat.seat_number || '',
                  isGeneralAdmission: seat.is_general_admission || false
                });
              } else if (seat.is_general_admission || seat.block_type === 'general_admission') {
                // General admission ticket
                const key = `ga_${seat.block_id}_${seat.ticket_type_id}`;
                const existing = seatsMap.get(key);
                if (existing && existing.isGeneralAdmission) {
                  // Update quantity if same ticket type exists
                  seatsMap.set(key, {
                    ...existing,
                    quantity: (existing.quantity || 0) + (seat.quantity || 1)
                  });
                } else {
                  seatsMap.set(key, {
                    isGeneralAdmission: true,
                    ticketTypeId: seat.ticket_type_id,
                    ticketTypeTitle: seat.ticket_type_name || 'Ticket',
                    blockId: seat.block_id,
                    blockName: seat.block_name,
                    quantity: seat.quantity || 1
                  });
                }
              }
            });
            const appliedAssignments = applyAssignmentsToSeats(seatsMap, loadPackageAssignments());
            setSelectedSeatsFromSelector(appliedAssignments.seatsMap);
            packageAssignmentsRef.current = appliedAssignments.assignments;
            setPackageAssignments(appliedAssignments.assignments);
            savePackageAssignments(appliedAssignments.assignments);
          }
        } catch (error) {
          Logger.error('EventSeating', 'init', error);
          props.common.showToast('Unable to load seating. Please try again.');
        }
      }

      // Load seating data and ticket types (pass orderId to ensure it's available)
      await loadSeatingAndPrices(currentOrderId)
        .finally(() => {
          props.common.hideLoading();
        });
    };

    initializeOrder();
  }, []); // eslint-disable-line

  // Function to load seating and ticket prices data
  const loadSeatingAndPrices = React.useCallback(async (orderIdToUse = null) => {
    if (!state.event.id) return false;

    const currentOrderId = orderIdToUse || orderId;
    if (!currentOrderId) return false;

    setIsLoadingSeatingData(true);
    setSeatingLoadFailed(false);
    setPricesLoadFailed(false);

    try {
      const [seatingRet, pricesRet] = await Promise.all([
        EventProvider.getSeating(state.event.id, currentOrderId),
        EventProvider.getEventPrices(state.event.id)
      ]);
      
      setIsLoadingSeatingData(false);
      
      let seatingDataLoaded = false;
      
      // Handle seating data
      if (!isValidProviderResult(seatingRet)) {
        Logger.error('EventSeating', 'getSeating', seatingRet.message || 'Unable to load seating');
        setSeatingLoadFailed(true);
      } else {
        setSeatingLoadFailed(false);
        seatingDataLoaded = true;
        setState(prev => ({
          ...prev,
          seating: seatingRet.data,
          loading: false,
        }));
      }

      // Handle ticket prices data
      let mappedTicketTypes = [];
      if (isValidProviderResult(pricesRet) && pricesRet.data && Array.isArray(pricesRet.data)) {
        setPricesLoadFailed(false);
        mappedTicketTypes = pricesRet.data.map(price => ({
          ticket_type_id: price.id,
          ticket_type_title: price.title,
          price: price.total_price,
          available_tickets: price.available_tickets
        }));
      } else {
        Logger.error('EventSeating', 'getEventPrices', pricesRet?.message || 'Unable to load ticket prices');
        setPricesLoadFailed(true);
      }

      setTicketTypes(mappedTicketTypes);
      
      // Return true if seating data loaded successfully
      return seatingDataLoaded && seatingRet.data && seatingRet.data.floor_plan_json;
    } catch (e) {
      setIsLoadingSeatingData(false);
      Logger.error('EventSeating', 'loadSeatingAndPrices', e);
      setSeatingLoadFailed(true);
      setPricesLoadFailed(true);
      return false;
    }
  }, [state.event.id, orderId]);

  React.useEffect(() => {
    if (!expiryTime) return;

    const setupExpiryCheck = () => {
      checkIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const secondsRemaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
        
        // Update the remaining time display
        setRemainingTime(formatTime(secondsRemaining));
        
        // Show warning modal when less than 2 minutes remain (and haven't shown it yet)
        if (secondsRemaining < 120 && secondsRemaining > 0 && !timeWarningShownRef.current) {
          setShowTimeWarningModal(true);
          timeWarningShownRef.current = true;
        }
        
        if (secondsRemaining <= 0) {
          // Session expired
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
          }
          Logger.error('EventSeating', 'checkExpiry', 'Seat selection session expired');
          props.common.navigate(`/payment-result/expired?order_id=${orderId}`);
        }
      }, 1000); // Update every second
    };
    
    setupExpiryCheck();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [expiryTime, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const rememberPackageAssignment = React.useCallback((seat, seatKey) => {
    if (!seat?.slotId) return;

    const next = {
      ...packageAssignmentsRef.current,
      [seat.slotId]: {
        seatKey,
        packageId: seat.packageId,
        packageIndex: seat.packageIndex,
        packageItemId: seat.packageItemId,
        packageName: seat.packageName,
        ticketTypeId: seat.ticketTypeId,
        isGeneralAdmission: !!seat.isGeneralAdmission,
        blockId: seat.blockId,
      },
    };

    packageAssignmentsRef.current = next;
    setPackageAssignments(next);
    savePackageAssignments(next);
  }, []);

  const forgetPackageAssignment = React.useCallback((seatKey, slotId) => {
    const next = { ...packageAssignmentsRef.current };

    if (slotId) {
      delete next[slotId];
    }

    Object.entries(next).forEach(([id, assignment]) => {
      if (assignment.seatKey === seatKey || String(assignment.seatKey) === String(seatKey)) {
        delete next[id];
      }
    });

    packageAssignmentsRef.current = next;
    setPackageAssignments(next);
    savePackageAssignments(next);
  }, []);

  const handlePayNow = async () => {
    if (!orderId || !state.event.id) {
      Logger.error('EventSeating', 'handlePayNow', 'Missing orderId or eventId');
      props.common.showToast('Unable to proceed to payment. Please try again.');
      return;
    }

    const hasSelectedPackage = Object.values(selectedPackages).some(pkg => pkg.count > 0);
    const packageSlots = buildPackageSlots(selectedPackages);
    const slotsFilled = packageSlots.every(slot => packageAssignments[slot.slotId]);
    const variantsSelected = arePackageVariantsSelected(eventPackages, selectedPackages, packageVariants);

    if (!hasSelectedPackage && selectedSeatsFromSelector.size === 0) {
      props.common.showToast('Please select at least one seat or ticket before proceeding to payment.');
      return;
    }

    if (!variantsSelected) {
      props.common.showToast('Please select sizes for all packages');
      return;
    }

    if (!slotsFilled) {
      props.common.showToast('Please select a seat for each ticket in your offer.');
      return;
    }

    try {

      // Get payment provider and token from query params
      const searchParams = new URLSearchParams(props.common.location.search);
      const token = searchParams.get('token');
      const provider = state.event?.payment_provider || DataStore.get('selectedEvent')?.payment_provider || 'PEACH';

      // Now proceed with payment checkout
      props.common.showLoading('Preparing your payment...');

      let currentUrl;
      if (props.common.isWidget) {
        currentUrl = `${window.location.origin}${window.location.pathname}#`;
      }

      // Extract general admission tickets from selectedSeatsFromSelector
      const generalAdmissionTickets = Array.from(selectedSeatsFromSelector.values())
        .filter(seatData => seatData.isGeneralAdmission && !seatData.slotId)
        .map(seatData => ({
          block_id: seatData.blockId,
          ticket_type_id: seatData.ticketTypeId,
          quantity: seatData.quantity || 1
        }));

      const customFields = DataStore.get('selectedEvent')?.custom_fields;
      const packagesPayload = buildPackagesCheckoutPayload(selectedPackages, selectedSeatsFromSelector);

      const paymentRes = await OrderProvider.createSeatsCheckout(
        orderId,
        currentUrl,
        generalAdmissionTickets,
        appliedCouponCode || null,
        Array.isArray(customFields) && customFields.length > 0 ? customFields : null,
        packagesPayload
      );

      if (paymentRes?.data?.skip_payment) {
        props.common.navigate(`/payment-result/success?order_id=${orderId}`);
        return;
      }

      // Handle different payment providers
      if (provider === 'YOCO' || provider === 'IKHOKHA') {
        // YOCO and IKHOKHA redirect to external payment page
        if (!isValidProviderResult(paymentRes) || !paymentRes.data.redirect_url) {
          throw Error(paymentRes?.message || 'Unable to create payment');
        }
        window.location.href = paymentRes.data.redirect_url;
        return;
      }

      // Handle PEACH and PAYSTACK
      if (provider === 'PEACH') {
        if (!isValidProviderResult(paymentRes) || !paymentRes.data.checkoutId || !paymentRes.data.entityId) {
          throw Error(paymentRes?.message || 'Unable to create payment');
        }
      } else {
        // PAYSTACK or other providers
        if (!isValidProviderResult(paymentRes) || !paymentRes.data.access_code) {
          throw Error(paymentRes?.message || 'Unable to create payment');
        }
      }

      // Calculate expiry time (10 minutes for seating)
      const minutes = 15;
      const expiryTime = new Date(Date.now() + minutes * 60 * 1000).getTime();

      // Build navigation URL based on provider
      let url = '';
      if (provider === 'PEACH') {
        url = `/payment.html?checkout_id=${paymentRes.data.checkoutId}&entity_id=${paymentRes.data.entityId}&expiry=${expiryTime}&orderId=${orderId}&token=${token}`;
        if (token) {
          url += `&token=${token}`;
        }
      } else {
        url = `/event/payment-frame?access_code=${paymentRes.data.access_code}&expiry=${expiryTime}&orderId=${orderId}`;
        if (token) {
          url += `&token=${token}`;
        }
      }

      if (provider === 'PEACH') {

        const contact = DataStore.get('LOGGED_IN_EMAIL') || '';
        if (contact) {
          url += `&contact=${encodeURIComponent(contact)}`;
        }

        const returnUrl = props.common.isWidget
          ? `${window.location.origin}${window.location.pathname}#`
          : window.location.origin;

        url += `&returnUrl=${encodeURIComponent(returnUrl)}`;

        window.location.href = `${Environment.paymentHost}${url}`;
        return;
      }

      props.common.navigate(url);

    } catch (error) {
      Logger.error('EventSeating', 'handlePayNow', error);
      props.common.hideLoading();
      props.common.showToast(error.message || 'Unable to proceed to payment. Please try again.');
    }
  };

  const handleSeatAdded = React.useCallback(async (seat) => {
      if (!orderId || !state.event.id) {
        Logger.error('EventSeating', 'handleSeatAdded', 'Missing orderId or eventId');
        return { ok: false };
      }

      try {
        // Handle general admission tickets differently (no seatId)
        if (seat.isGeneralAdmission) {
          const isPackageSeat = !!seat.slotId;
          const key = isPackageSeat ? `ga_pkg_${seat.slotId}` : `ga_${seat.blockId}_${seat.ticketTypeId}`;

          setSelectedSeatsFromSelector(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(key);

            if (!isPackageSeat && existing && existing.isGeneralAdmission) {
              newMap.set(key, {
                ...existing,
                quantity: (seat.quantity || 1)
              });
            } else {
              newMap.set(key, {
                isGeneralAdmission: true,
                ticketTypeId: seat.ticketTypeId,
                ticketTypeTitle: seat.ticketTypeTitle || 'Ticket',
                blockId: seat.blockId,
                blockName: seat.blockName,
                quantity: isPackageSeat ? 1 : (seat.quantity || 1),
                slotId: seat.slotId,
                packageId: seat.packageId,
                packageIndex: seat.packageIndex,
                packageItemId: seat.packageItemId,
                packageName: seat.packageName,
              });
            }
            return applyCouponToSelectedTickets(newMap, appliedCouponItems);
          });

          if (isPackageSeat) {
            rememberPackageAssignment(seat, key);
          }

          return { ok: true };
        }

        // Handle regular seat selection
        const result = await OrderProvider.reserveSeat(
          state.event.id,
          seat.seatId,
          seat.ticketTypeId,
          'reserve',
          parseInt(orderId)
        );

        if (!isValidProviderResult(result)) {
          Logger.error('EventSeating', 'handleSeatAdded', result.message || 'Unable to reserve seat');
          props.common.showToast(result.message || 'Unable to reserve seat. Please try again.');
          return result;
        }

        // Update selected seats summary
        setSelectedSeatsFromSelector(prev => {
          const newMap = new Map(prev);
          newMap.set(seat.seatId, {
            ticketTypeId: seat.ticketTypeId,
            ticketTypeTitle: seat.ticketTypeTitle || 'Ticket',
            blockId: seat.blockId,
            blockName: seat.blockName,
            seatRow: seat.seatRow,
            seatNumber: seat.seatNumber,
            slotId: seat.slotId,
            packageId: seat.packageId,
            packageIndex: seat.packageIndex,
            packageItemId: seat.packageItemId,
            packageName: seat.packageName,
          });
          return applyCouponToSelectedTickets(newMap, appliedCouponItems);
        });

        if (seat.slotId) {
          rememberPackageAssignment(seat, seat.seatId);
        }

        // Refresh seating data to get updated statuses
        //await refreshSeatingData();
        return { ok: true };
      } catch (error) {
        Logger.error('EventSeating', 'handleSeatAdded', error);
        props.common.showToast(error.message || 'Unable to reserve seat. Please try again.');
        return { ok: false };
      }
  }, [orderId, state.event.id, props.common, appliedCouponItems, applyCouponToSelectedTickets, rememberPackageAssignment]);

  const handleSeatRemoved = React.useCallback(async (seat) => {
      if (!orderId || !state.event.id) {
        Logger.error('EventSeating', 'handleSeatRemoved', 'Missing orderId or eventId');
        return { ok: false };
      }

      try {
        // Handle general admission tickets differently
        if (seat.isGeneralAdmission) {
          const result = await OrderProvider.reserveGeneralAdmissionSeat(
            state.event.id,
            seat.blockId,
            seat.ticketTypeId,
            'unreserve',
            parseInt(orderId)
          );

          if (!isValidProviderResult(result)) {
            Logger.error('EventSeating', 'handleSeatRemoved', result.message || 'Unable to remove general admission ticket');
            props.common.showToast(result.message || 'Unable to remove ticket. Please try again.');
            return { ok: false };
          }

          // Remove from summary
          setSelectedSeatsFromSelector(prev => {
            const newMap = new Map(prev);
            const key = seat.slotId ? `ga_pkg_${seat.slotId}` : `ga_${seat.blockId}_${seat.ticketTypeId}`;
            newMap.delete(key);
            return applyCouponToSelectedTickets(newMap, appliedCouponItems);
          });
          forgetPackageAssignment(seat.slotId ? `ga_pkg_${seat.slotId}` : null, seat.slotId);
          return { ok: true };
        }

        // Handle regular seat removal
        // ticketTypeId may be undefined for seats that were reserved by the current order but not in selectedSeats
        const result = await OrderProvider.reserveSeat(
          state.event.id,
          seat.seatId,
          seat.ticketTypeId || null, // Allow null/undefined for unreserve
          'unreserve',
          parseInt(orderId)
        );

        if (!isValidProviderResult(result)) {
          Logger.error('EventSeating', 'handleSeatRemoved', result.message || 'Unable to unreserve seat');
          props.common.showToast(result.message || 'Unable to unreserve seat. Please try again.');
          return { ok: false };
        }

        // Update selected seats summary
        setSelectedSeatsFromSelector(prev => {
          const newMap = new Map(prev);
          newMap.delete(seat.seatId);
          return applyCouponToSelectedTickets(newMap, appliedCouponItems);
        });
        forgetPackageAssignment(seat.seatId, seat.slotId);

        // Refresh seating data to get updated statuses
        //await refreshSeatingData();
        return { ok: true };
      } catch (error) {
        Logger.error('EventSeating', 'handleSeatRemoved', error);
        props.common.showToast(error.message || 'Unable to unreserve seat. Please try again.');
        return { ok: false };
      }
  }, [orderId, state.event.id, props.common, appliedCouponItems, applyCouponToSelectedTickets, forgetPackageAssignment]);

  const handleConfirmRemove = React.useCallback(async () => {
    if (!pendingRemoval) return;
    try {
      props.common.showLoading('Removing seat...');
      const result = await handleSeatRemoved(pendingRemoval);
      props.common.hideLoading();
      if (result && result.ok) {
        // Seat/ticßket will be removed from summary by handleSeatRemoved
      }
    } finally {
      props.common.hideLoading();
    }
    setShowRemoveConfirmModal(false);
    setPendingRemoval(null);
  }, [pendingRemoval, handleSeatRemoved, props.common]);

  const handleCancelRemove = React.useCallback(() => {
    setShowRemoveConfirmModal(false);
    setPendingRemoval(null);
  }, []);

  React.useEffect(() => {
    if (!state.event?.id) return;

    EventProvider.getPackagesByEventId(state.event.id)
      .then(ret => {
        if (!isValidProviderResult(ret) || !Array.isArray(ret.data)) {
          setEventPackages([]);
          return;
        }

        setEventPackages(ret.data);

        const stored = DataStore.get('selectedTickets')?.selectedPackages || {};
        const valid = {};

        ret.data.forEach(packageData => {
          const storedPackage = stored[packageData.id];
          if (!storedPackage?.count) return;

          valid[packageData.id] = {
            ...storedPackage,
            packageId: packageData.id,
            name: packageData.name,
            price: packageData.price,
            ticketItems: getPackageTicketItems(packageData),
          };
        });

        setSelectedPackages(current => {
          if (Object.keys(current).length > 0) return current;
          setPackageVariants(variantsStateFromPackages(valid));
          writeSelectedPackages(valid);
          return valid;
        });
      })
      .catch(error => Logger.error('EventSeating', 'getPackagesByEventId', error));
  }, [state.event?.id]);

  const releasePackageSlots = React.useCallback(async (slots) => {
    const assignedSeats = [];
    const generalAdmissionSeats = [];

    for (const slot of slots) {
      const assignment = packageAssignmentsRef.current[slot.slotId];
      if (!assignment) continue;

      if (assignment.isGeneralAdmission) {
        generalAdmissionSeats.push({ slot, assignment });
      } else {
        assignedSeats.push({ slot, assignment });
      }
    }

    if ((assignedSeats.length > 0 || generalAdmissionSeats.length > 0) && (!orderId || !state.event.id)) {
      return false;
    }

    if (assignedSeats.length > 0) {
      const result = await OrderProvider.unreserveSeats(
        state.event.id,
        parseInt(orderId),
        assignedSeats.map(({ assignment }) => assignment.seatKey)
      );

      if (!isValidProviderResult(result)) {
        Logger.error('EventSeating', 'releasePackageSlots', result.message || 'Unable to release seats');
        return false;
      }

      setSelectedSeatsFromSelector(prev => {
        const next = new Map(prev);
        assignedSeats.forEach(({ assignment }) => {
          const key = findSeatKey(next, assignment.seatKey);
          if (key != null) next.delete(key);
        });
        return applyCouponToSelectedTickets(next, appliedCouponItems);
      });
      assignedSeats.forEach(({ slot, assignment }) => {
        forgetPackageAssignment(assignment.seatKey, slot.slotId);
      });
    }

    for (const { slot, assignment } of generalAdmissionSeats) {
      const result = await OrderProvider.reserveGeneralAdmissionSeat(
        state.event.id,
        assignment.blockId,
        assignment.ticketTypeId,
        'unreserve',
        parseInt(orderId)
      );

      if (!isValidProviderResult(result)) {
        Logger.error('EventSeating', 'releasePackageSlots', result.message || 'Unable to release seat');
        return false;
      }

      setSelectedSeatsFromSelector(prev => {
        const next = new Map(prev);
        const key = findSeatKey(next, assignment.seatKey);
        if (key != null) next.delete(key);
        return applyCouponToSelectedTickets(next, appliedCouponItems);
      });
      forgetPackageAssignment(assignment.seatKey, slot.slotId);
    }

    return true;
  }, [orderId, state.event.id, appliedCouponItems, applyCouponToSelectedTickets, forgetPackageAssignment]);

  const removePackageCopy = async ({ packageId, copyIndex }) => {
    if (packageUpdateLock.current) {
      props.common.showToast('Please wait for the current update to finish.');
      return false;
    }

    const previous = selectedPackages[packageId];
    if (!previous) return false;

    packageUpdateLock.current = true;

    try {
      const copySlots = buildPackageSlots({ [packageId]: previous }).filter(slot => slot.copyIndex === copyIndex);
      const released = await releasePackageSlots(copySlots);
      if (!released) {
        props.common.showToast('Unable to remove this package. Please try again.');
        return false;
      }

      const nextAssignments = {};
      const seatUpdates = [];

      Object.entries(packageAssignmentsRef.current).forEach(([slotId, assignment]) => {
        if (String(assignment.packageId) !== String(packageId) || assignment.packageIndex < copyIndex) {
          nextAssignments[slotId] = assignment;
          return;
        }

        if (assignment.packageIndex === copyIndex) return;

        const newIndex = assignment.packageIndex - 1;
        const newSlotId = `${packageId}:${newIndex}:${assignment.packageItemId}`;
        const newSeatKey = assignment.isGeneralAdmission ? `ga_pkg_${newSlotId}` : assignment.seatKey;
        nextAssignments[newSlotId] = {
          ...assignment,
          packageIndex: newIndex,
          seatKey: newSeatKey,
        };
        seatUpdates.push({
          oldSeatKey: assignment.seatKey,
          oldSlotId: slotId,
          newSlotId,
          newIndex,
          newSeatKey,
          isGeneralAdmission: assignment.isGeneralAdmission,
        });
      });

      packageAssignmentsRef.current = nextAssignments;
      setPackageAssignments(nextAssignments);
      savePackageAssignments(nextAssignments);

      if (seatUpdates.length > 0) {
        setSelectedSeatsFromSelector(prev => {
          const next = new Map(prev);
          seatUpdates.forEach(update => {
            const key = update.isGeneralAdmission
              ? (next.has(`ga_pkg_${update.oldSlotId}`) ? `ga_pkg_${update.oldSlotId}` : findSeatKey(next, update.oldSeatKey))
              : findSeatKey(next, update.oldSeatKey);
            if (key == null) return;
            const seat = next.get(key);
            next.delete(key);
            next.set(update.isGeneralAdmission ? update.newSeatKey : key, {
              ...seat,
              slotId: update.newSlotId,
              packageIndex: update.newIndex,
            });
          });
          return next;
        });
      }

      const newCount = previous.count - 1;
      const nextPackages = { ...selectedPackages };
      const nextVariants = {};

      Object.entries(packageVariants).forEach(([key, value]) => {
        const prefix = `${packageId}-`;
        if (!key.startsWith(prefix)) {
          nextVariants[key] = value;
          return;
        }

        const rest = key.slice(prefix.length);
        const lastDash = rest.lastIndexOf('-');
        const index = parseInt(rest.slice(lastDash + 1), 10);
        if (index === copyIndex) return;

        const newIndex = index > copyIndex ? index - 1 : index;
        const itemId = rest.slice(0, lastDash);
        nextVariants[`${packageId}-${itemId}-${newIndex}`] = value;
      });

      if (newCount <= 0) {
        delete nextPackages[packageId];
      } else {
        const shiftedVariants = {};
        Object.entries(previous.variants || {}).forEach(([itemId, byIndex]) => {
          const shifted = {};
          Object.entries(byIndex || {}).forEach(([index, info]) => {
            const parsed = parseInt(index, 10);
            if (parsed === copyIndex) return;
            shifted[parsed > copyIndex ? parsed - 1 : parsed] = info;
          });
          if (Object.keys(shifted).length > 0) shiftedVariants[itemId] = shifted;
        });

        nextPackages[packageId] = {
          ...previous,
          count: newCount,
          totalPrice: newCount * Number(previous.price || 0),
          variants: shiftedVariants,
        };
      }

      setPackageVariants(nextVariants);
      setSelectedPackages(nextPackages);
      writeSelectedPackages(nextPackages);
      setPackageCounterEpoch(epoch => epoch + 1);
      return true;
    } finally {
      packageUpdateLock.current = false;
    }
  };

  const handleCancelRemovePackage = () => {
    setShowRemovePackageModal(false);
    setPendingPackageRemoval(null);
  };

  const handleConfirmRemovePackage = async () => {
    if (!pendingPackageRemoval) return;
    const removal = pendingPackageRemoval;
    props.common.showLoading('Removing package...');
    try {
      const removed = await removePackageCopy(removal);
      if (!removed) return;
      setShowRemovePackageModal(false);
      setPendingPackageRemoval(null);
    } finally {
      props.common.hideLoading();
    }
  };

  const onPackageCounterChange = async (count, packageData) => {
    if (!packageData) return;

    if (packageUpdateLock.current) {
      setPackageCounterEpoch(epoch => epoch + 1);
      return;
    }

    packageUpdateLock.current = true;

    try {
      const packageId = packageData.id || 'package-1';
      const previous = selectedPackages[packageId];
      const removedSlots = slotsAboveCount(previous || {
        packageId,
        name: packageData.name,
        count: previous?.count || 0,
        ticketItems: getPackageTicketItems(packageData),
      }, count);

      if (removedSlots.length > 0) {
        const released = await releasePackageSlots(removedSlots);
        if (!released) {
          props.common.showToast('Unable to update this offer. Please try again.');
          setPackageCounterEpoch(epoch => epoch + 1);
          return;
        }
      }

      const nextPackages = { ...selectedPackages };
      const nextVariants = { ...packageVariants };

      if (count === 0) {
        delete nextPackages[packageId];
        Object.keys(nextVariants).forEach(key => {
          if (key.startsWith(`${packageId}-`)) delete nextVariants[key];
        });
      } else {
        Object.keys(nextVariants).forEach(key => {
          if (!key.startsWith(`${packageId}-`)) return;
          const index = parseInt(key.split('-').pop(), 10);
          if (index > count) delete nextVariants[key];
        });

        nextPackages[packageId] = {
          name: packageData.name,
          packageId,
          count,
          price: packageData.price,
          totalPrice: count * packageData.price,
          variants: filterVariantsForCount(previous?.variants || {}, count),
          ticketItems: getPackageTicketItems(packageData),
        };
      }

      setPackageVariants(nextVariants);
      setSelectedPackages(nextPackages);
      writeSelectedPackages(nextPackages);
    } finally {
      packageUpdateLock.current = false;
    }
  };

  const onPackageVariantSelect = (packageId, itemId, packageIndex, variantId) => {
    const packageData = eventPackages.find(pkg => String(pkg.id) === String(packageId));
    if (!packageData?.items) return;

    const productItem = packageData.items.find(item =>
      (String(item.id) === String(itemId) || String(item.item_id) === String(itemId)) &&
      item.item_type === 'product_id' &&
      item.item_details?.variants?.length > 0
    );
    if (!productItem?.item_details) return;

    const variant = productItem.item_details.variants.find(item => item.option_id === variantId);
    if (!variant) return;

    const variantKey = `${packageId}-${itemId}-${packageIndex}`;
    setPackageVariants(prev => ({
      ...prev,
      [variantKey]: variant,
    }));

    const nextPackages = { ...selectedPackages };
    if (!nextPackages[packageId]) return;

    const itemIdStr = String(itemId);
    const itemVariants = nextPackages[packageId].variants?.[itemIdStr] || {};
    nextPackages[packageId] = {
      ...nextPackages[packageId],
      variants: {
        ...nextPackages[packageId].variants,
        [itemIdStr]: {
          ...itemVariants,
          [packageIndex]: {
            option_id: variant.option_id,
            option_value: variant.option_value,
            product_variant_id: variant.product_variant_id,
            delivery_type: productItem.item_details.delivery_type,
          },
        },
      },
    };

    setSelectedPackages(nextPackages);
    writeSelectedPackages(nextPackages);
  };

  const formatSeatLabel = (seatId, seatData) => {
    if (seatData?.isGeneralAdmission) {
      const quantity = seatData.quantity || 1;
      const name = seatData.blockName || 'General Admission';
      return quantity > 1 ? `${name} (x${quantity})` : name;
    }

    if (seatData?.seatRow || seatData?.seatNumber) {
      const seatLabel = `${seatData.seatRow || ''}${seatData.seatNumber || ''}`;
      return seatData.blockName ? `${seatData.blockName} - Seat ${seatLabel}` : seatLabel;
    }

    if (seatData?.blockName) return `${seatData.blockName} - Seat ${seatId}`;
    return `Seat ${seatId}`;
  };

  const handleRemoveClick = React.useCallback((seatData) => {
    setPendingRemoval(seatData);
    setShowRemoveConfirmModal(true);
  }, []);

  // Handle general admission quantity change (+ or -)
  const handleGeneralAdmissionQuantityChange = React.useCallback(async (seatData, delta) => {
    if (!orderId || !state.event.id) {
      Logger.error('EventSeating', 'handleGeneralAdmissionQuantityChange', 'Missing orderId or eventId');
      return;
    }

    const currentQuantity = seatData.quantity || 1;
    const newQuantity = currentQuantity + delta;
    
    if (newQuantity < 0) return; // Can't go below 0
    
    const action = delta > 0 ? 'reserve' : 'unreserve';

    try {
      setIsUpdatingQuantity(true);

      const result = await OrderProvider.reserveGeneralAdmissionSeat(
        state.event.id,
        seatData.blockId,
        seatData.ticketTypeId,
        action,
        parseInt(orderId)
      );

      if (!isValidProviderResult(result)) {
        Logger.error('EventSeating', 'handleGeneralAdmissionQuantityChange', result.message || 'Unable to update quantity');
        props.common.showToast(result.message || 'Unable to update quantity. Please try again.');
        return;
      }

      // Update the quantity in selectedSeatsFromSelector
      setSelectedSeatsFromSelector(prev => {
        const newMap = new Map(prev);
        const key = `ga_${seatData.blockId}_${seatData.ticketTypeId}`;
        const existing = newMap.get(key);
        
        if (newQuantity === 0) {
          // Remove if quantity reaches 0
          newMap.delete(key);
        } else if (existing) {
          // Update quantity
          newMap.set(key, {
            ...existing,
            quantity: newQuantity
          });
        }
        return newMap;
      });
    } catch (error) {
      Logger.error('EventSeating', 'handleGeneralAdmissionQuantityChange', error);
      props.common.showToast(error.message || 'Unable to update quantity. Please try again.');
    }
    finally {
      setIsUpdatingQuantity(false);
    }
  }, [orderId, state.event.id, props.common]);

  const openSeatSelector = async (packageId = null, copyIndex = null) => {
    setActivePackageSeat(packageId == null ? null : { packageId, copyIndex });

    if (seatingLoadFailed || pricesLoadFailed || !state.seating) {
      props.common.showLoading('Loading seat map...');
      const success = await loadSeatingAndPrices(orderId);
      props.common.hideLoading();

      if (success) {
        setIsSeatSelectorOpen(true);
      } else {
        setActivePackageSeat(null);
        props.common.showToast('Unable to load seat map. Please try again.');
      }
      return;
    }

    setIsSeatSelectorOpen(true);
  };

  const closeSeatSelector = () => {
    setIsSeatSelectorOpen(false);
    setActivePackageSeat(null);
  };

  const renderSeatingMap = () => {
    if (state.loading) {
      return (
        <CenterContainer padding={'40px 20px'}>
          <Text>Loading seat map...</Text>
        </CenterContainer>
      );
    }

    if (!state.seating) {
      return (
        <CenterContainer padding={'40px 20px'}>
          <Text>No seating information available</Text>
        </CenterContainer>
      );
    }

    if (isSeatSelectorOpen) {
      // Only render SeatSelector if we have seating data
      if (!state.seating || !state.seating.floor_plan_json) {
        return null;
      }
      
      return (
        <ErrorBoundary
          onError={(error) => Logger.error('EventSeating', 'SeatSelector', error)}
          fallback={
            <SeatMapOverlay>
              <SeatMapErrorReloadContainer>
                <Text color={Theme.Text.Color} textAlign={'center'} fontSize={FontSizes.Small}>
                  The seat map could not be loaded. Reload the page to try again.
                </Text>
                <Button
                  text="Reload page"
                  width={'180px'}
                  onClick={() => window.location.reload()}
                />
              </SeatMapErrorReloadContainer>
            </SeatMapOverlay>
          }
        >
          <Suspense fallback={
            <SeatMapOverlay>
              <Loader backgroundColor="#ffffff" />
            </SeatMapOverlay>
          }>
            <SeatSelector
              seatMapData={state.seating.floor_plan_json}
              ticketTypes={ticketTypes}
              eventId={state.event.id}
              orderId={orderId}
              onSeatAdded={handleSeatAdded}
              onSeatRemoved={handleSeatRemoved}
              onClose={closeSeatSelector}
              timeRemaining={remainingTime}
              selectedGeneralAdmissionTickets={selectedSeatsFromSelector}
              packageSlots={activePackageSeat == null ? [] : buildPackageSlots(selectedPackages).filter(slot =>
                String(slot.packageId) === String(activePackageSeat.packageId) &&
                slot.copyIndex === activePackageSeat.copyIndex &&
                !packageAssignments[slot.slotId]
              )}
              hideIndividualTickets={activePackageSeat != null}
              common={props.common}
            />
          </Suspense>
        </ErrorBoundary>
      );
    }

    return null;
  };

  const getCouponRowPrices = (seatData, ticketType, isGeneralAdmission) => {
    const unit = ticketType ? (ticketType.total_price ?? ticketType.price ?? 0) : 0;
    const qty = isGeneralAdmission ? (seatData.quantity || 1) : 1;
    const original = unit * qty;
    const hasCoupon =
      !!appliedCouponCode &&
      seatData.coupon_applied &&
      seatData.coupon_applied_price != null &&
      !Number.isNaN(Number(seatData.coupon_applied_price));
    const unitDiscounted = hasCoupon ? Number(seatData.coupon_applied_price) : unit;
    const discounted = unitDiscounted * qty;
    const showPair = hasCoupon && Math.abs(original - discounted) > 0.005;
    return { original, discounted, showPair };
  };

  const renderRowPriceDisplay = (seatData, ticketType, isGeneralAdmission) => {
    if (!ticketType || (ticketType.total_price === undefined && ticketType.price === undefined)) {
      return null;
    }
    const { original, discounted, showPair } = getCouponRowPrices(seatData, ticketType, isGeneralAdmission);
    if (showPair) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <TicketSummaryPrice style={{ textDecoration: 'line-through', opacity: 0.65, fontSize: FontSizes.Smallest }}>
            R {original.toFixed(2)}
          </TicketSummaryPrice>
          <TicketSummaryPrice>
            R {discounted.toFixed(2)}
          </TicketSummaryPrice>
        </div>
      );
    }
    return <TicketSummaryPrice>R {original.toFixed(2)}</TicketSummaryPrice>;
  };

  const selectedPackageList = Object.values(selectedPackages).filter(pkg => pkg.count > 0);
  const individualSeatEntries = Array.from(selectedSeatsFromSelector.entries())
    .filter(([, seatData]) => !seatData.slotId);
  const packageSlots = buildPackageSlots(selectedPackages);
  const packageSlotsFilled = packageSlots.every(slot => packageAssignments[slot.slotId]);
  const packageVariantsSelected = arePackageVariantsSelected(eventPackages, selectedPackages, packageVariants);
  const hasOrderLines = individualSeatEntries.length > 0 || selectedPackageList.length > 0 || selectedSeatsFromSelector.size > 0;

  return (
    <>
      <Text
        maxWidth={'1100px'}
        margin={'0 auto'}
        width={'100%'}
        display={'block'} 
        padding={'20px 15px 0 15px'} 
        fontSize={FontSizes.Small}
        color={Theme.Text.Color}
      >
        <span style={{cursor: 'pointer'}} onClick={() => props.common.navigate(-1)}>← Back</span>
      </Text>

      <EventName>{state.event.name}</EventName>

      <Text display={'block'} padding={'0 15px 5px 15px'} textAlign={'center'} fontSize={FontSizes.Small}>
        {state.event.location_display}
      </Text>

      {!state.event.is_multi_event && (
        <Text display={'block'} padding={'0 15px 20px 15px'} textAlign={'center'} fontSize={FontSizes.Small}>
          {state.event.displayDate}
        </Text>
      )}

      {state.event.is_multi_event && (
        <Text display={'block'} padding={'0 0 30px 0'} textAlign={'center'} fontSize={FontSizes.Small}></Text>
      )}

      <FullWidthContainer>
        <StepperContainer>
          <Stepper
            items={getCheckoutSteps(state.event)}
            selectedIndex={2}
          />
        </StepperContainer>

        <Text fontSize={FontSizes.Small} textAlign={'center'} padding={'10px 15px 0 15px'}>Please select your seats before the time runs out.</Text>
        <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Big} textAlign={'center'} padding={'0 15px 0 15px'}>{remainingTime}</Text>

        {eventPackages.length > 0 && (
          <TicketSummaryContainer noMarginBottom>
            <CouponSectionHeader>Packages</CouponSectionHeader>
            <Divider />
            {eventPackages.map(packageData => {
              const packageId = packageData.id || 'package-1';
              const packageCount = selectedPackages[packageId]?.count || 0;
              const productItems = getPackageProductItems(packageData);
              const packageIndices = Array.from({ length: packageCount }, (_, index) => index + 1);

              const isExpanded = !!expandedOfferIds[packageId];
              const includedItems = [];

              (packageData.items || []).forEach(item => {
                const itemName = item.item_type === 'product_id'
                  ? (item.item_details?.product_name || 'Product')
                  : item.item_type === 'event_price_id'
                    ? (item.item_details?.title || 'Ticket')
                    : '';
                if (!itemName) return;

                const groupKey = `${item.item_type}:${item.item_id ?? item.item_details?.id ?? itemName}`;
                const existing = includedItems.find(entry => entry.groupKey === groupKey);
                const quantity = Number(item.quantity) || 1;

                if (existing) {
                  existing.count += quantity;
                } else {
                  includedItems.push({ groupKey, itemName, count: quantity });
                }
              });

              return (
                <OfferBlock key={packageId} $first={eventPackages[0] === packageData}>
                  <OfferPriceRow>
                    <div>
                    <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Regular}>{packageData.name}</Text>
                    {!!packageData.description && (
                      <Text display={'block'} fontSize={FontSizes.Small}>{packageData.description}</Text>
                    )}
                    </div>
                    <NumericSelect
                      key={`${packageId}-${packageCount}-${packageCounterEpoch}`}
                      defaultValue={packageCount}
                      onCounterChange={(count) => onPackageCounterChange(count, packageData)}
                    />
                  </OfferPriceRow>
                  {!!packageData.items?.length && (
                    <>
                      <OfferShowMore
                        type="button"
                        onClick={() => setExpandedOfferIds(prev => ({
                          ...prev,
                          [packageId]: !prev[packageId],
                        }))}
                      >
                        {isExpanded ? 'Show less' : 'Show more'}
                      </OfferShowMore>
                      {isExpanded && (
                        <div>
                          <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Small} padding={'0 0 8px 0'}>
                            Package Includes:
                          </Text>
                          {includedItems.map(item => (
                            <IncludedItemRow key={`${packageId}-included-${item.groupKey}`}>
                              <Text fontSize={FontSizes.Small} color={Colors.PrimaryOne}>✓</Text>
                              <Text fontSize={FontSizes.Small}>{`${item.count} x ${item.itemName}`}</Text>
                            </IncludedItemRow>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {packageCount > 0 && productItems.map((productItem, itemIndex) => {
                    const itemId = packageItemId(productItem, itemIndex);
                    const variants = productItem.item_details.variants;

                    return (
                      <div key={`${packageId}-variants-${itemId}`}>
                        <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Small} padding={'4px 0 8px 0'}>
                          {productItem.item_details?.product_name}
                        </Text>
                        {packageIndices.map(index => {
                          const variantKey = `${packageId}-${itemId}-${index}`;
                          const selectedVariant = packageVariants[variantKey];

                          return (
                            <div key={variantKey} style={{ marginBottom: '12px' }}>
                              <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Smaller} padding={'0 0 6px 0'}>
                                Package {index}:
                              </Text>
                              <VariantButtonRow>
                                {variants.map(variant => {
                                  const isSelected = selectedVariant?.option_id === variant.option_id;
                                  const outOfStock = variant.available_stock <= 0;

                                  return (
                                    <VariantButton
                                      key={variant.option_id}
                                      $selected={isSelected}
                                      disabled={outOfStock}
                                      type="button"
                                      onClick={() => !outOfStock && onPackageVariantSelect(packageId, itemId, index, variant.option_id)}
                                    >
                                      {variant.option_value}
                                    </VariantButton>
                                  );
                                })}
                              </VariantButtonRow>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </OfferBlock>
              );
            })}
            <OfferBlock $first={false}>
              <OfferPriceRow>
                <div>
                  <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Regular}>Standard seats</Text>
                  <Text display={'block'} fontSize={FontSizes.Small}>Select individual seats not part of a package.</Text>
                </div>
                <Button
                  onClick={() => openSeatSelector(null)}
                  disabled={isLoadingSeatingData}
                  height={'30px'}
                  width={'30px'}
                  minWidth={'30px'}
                >
                  +
                </Button>
              </OfferPriceRow>
            </OfferBlock>
          </TicketSummaryContainer>
        )}

        {!!state.event.accept_coupons && (
          <TicketSummaryContainer noMarginBottom>
            <CouponSection style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              <CouponSectionHeader>Redeem Coupon or Discount Code</CouponSectionHeader>
              <PSmall padding={'0 0 12px 0'} color={Theme.Text.Color}>
                If you have a coupon or discount code, enter it below to apply a discount to your order.
              </PSmall>
              {!appliedCouponCode ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <CouponRow>
                    <CouponInputWrap>
                      <TextInput
                        placeholder="Enter coupon code"
                        margin={'0'}
                        value={couponCode}
                        onChange={(value) => {
                          setCouponCode(value.toUpperCase());
                          setCouponError('');
                        }}
                        width="100%"
                        hasError={!!couponError}
                        errorMessage={couponError}
                      />
                    </CouponInputWrap>
                    <Button
                      text="Apply"
                      onClick={onApplyCoupon}
                      showLoading={isApplyingCoupon}
                      width="100px"
                      height="40px"
                    />
                  </CouponRow>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <Text fontSize={FontSizes.Small} fontFamily={FontFamilies.Bold}>
                    Coupon applied: {appliedCouponCode}
                  </Text>
                  <Button
                    text="Remove"
                    onClick={onRemoveCoupon}
                    width="100px"
                    height="40px"
                  />
                </div>
              )}
            </CouponSection>
          </TicketSummaryContainer>
        )}

        {/* Ticket Selection Summary */}
        <TicketSummaryContainer>
          <TicketSummaryTitle>Selected Seats</TicketSummaryTitle>
          {selectedPackageList.length === 0 && individualSeatEntries.length === 0 ? (
            <EmptySummaryText>No seats selected yet</EmptySummaryText>
          ) : (
            <TicketSummaryList>
              {selectedPackageList.map(pkg => {
                const slots = buildPackageSlots({ [pkg.packageId]: pkg });
                const copies = Array.from({ length: pkg.count }, (_, index) => index + 1);

                return copies.map(copyIndex => {
                  const copySlots = slots.filter(slot => slot.copyIndex === copyIndex);
                  const seatsSelected = copySlots.length > 0 && copySlots.every(slot => packageAssignments[slot.slotId]);

                  return (
                    <PackageSeatGroup key={`package-summary-${pkg.packageId}-${copyIndex}`}>
                      <PackageCopyHeader>
                        <PackageCopyInfo>
                          <PackageCopyTitleRow>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <TicketSummarySeatId>{pkg.name}</TicketSummarySeatId>
                              <TicketSummaryType>{`Package ${copyIndex}`}</TicketSummaryType>
                            </div>
                          </PackageCopyTitleRow>
                        </PackageCopyInfo>
                        <PackageCopyActions>
                          <TicketSummaryPrice>R {Number(pkg.price || 0).toFixed(2)}</TicketSummaryPrice>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {copySlots.length > 0 && (
                              <PackageSeatButtonWrap>
                                <Button
                                  text={seatsSelected ? 'Edit seats' : (isLoadingSeatingData ? 'Loading...' : 'Select Seats')}
                                  onClick={() => openSeatSelector(pkg.packageId, copyIndex)}
                                  disabled={isLoadingSeatingData}
                                  width="100%"
                                  height="36px"
                                  backgroundColor={'#FFFFFF'}
                                  textColor={Theme.Text.Color || '#3498db'}
                                  border={'1px solid ' + (Theme.Text.Color || '#3498db')}
                                  fontFamily={FontFamilies.Bold}
                                />
                              </PackageSeatButtonWrap>
                            )}
                            <PackageRemoveButton
                              type="button"
                              onClick={() => {
                                setPendingPackageRemoval({
                                  packageId: pkg.packageId,
                                  copyIndex,
                                  name: pkg.name,
                                });
                                setShowRemovePackageModal(true);
                              }}
                              title="Remove package"
                              aria-label="Remove package"
                            >
                              <TrashIcon />
                            </PackageRemoveButton>
                          </div>
                        </PackageCopyActions>
                      </PackageCopyHeader>
                      {copySlots.length > 0 && (
                        <PackageSeatList>
                          {copySlots.map(slot => {
                            const assignment = packageAssignments[slot.slotId];
                            const seatKey = assignment ? findSeatKey(selectedSeatsFromSelector, assignment.seatKey) : null;
                            const seatData = seatKey != null ? selectedSeatsFromSelector.get(seatKey) : null;

                            return (
                              <TicketSummaryItem key={slot.slotId} padding="8px">
                                <TicketSummaryInfo>
                                  <TicketSummarySeatId color={seatData ? Theme.Text.Color : '#e74c3c !important'}>
                                    {seatData ? formatSeatLabel(seatKey, seatData) : 'Seat not selected'}
                                  </TicketSummarySeatId>
                                  <TicketSummaryType>{slot.ticketTypeTitle}</TicketSummaryType>
                                </TicketSummaryInfo>
                                {seatData && (
                                  <TicketSummaryRemoveButton
                                    onClick={() => handleRemoveClick(seatData.isGeneralAdmission ? {
                                      isGeneralAdmission: true,
                                      blockId: seatData.blockId,
                                      ticketTypeId: seatData.ticketTypeId,
                                      slotId: slot.slotId,
                                    } : {
                                      seatId: seatKey,
                                      blockId: seatData.blockId,
                                      ticketTypeId: seatData.ticketTypeId,
                                      slotId: slot.slotId,
                                    })}
                                    title="Remove seat"
                                  >
                                    ×
                                  </TicketSummaryRemoveButton>
                                )}
                              </TicketSummaryItem>
                            );
                          })}
                        </PackageSeatList>
                      )}
                    </PackageSeatGroup>
                  );
                });
              })}
              {individualSeatEntries.map(([seatId, seatData]) => {
                const ticketType = ticketTypes.find(tt => tt.ticket_type_id === seatData.ticketTypeId);
                
                // Handle general admission tickets
                if (seatData.isGeneralAdmission) {
                  const quantity = seatData.quantity || 1;
                  
                  return (
                    <TicketSummaryItem key={seatId}>
                      <TicketSummaryInfo>
                        <TicketSummarySeatId>
                          {seatData.blockName || 'General Admission'} {quantity > 1 && `(x${quantity})`}
                        </TicketSummarySeatId>
                        <TicketSummaryType>{seatData.ticketTypeTitle || ticketType?.title || 'Ticket'}</TicketSummaryType>
                      </TicketSummaryInfo>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {renderRowPriceDisplay(seatData, ticketType, true)}
                        {quantity > 1 ? (
                          // Show + and - buttons when quantity > 1
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Button
                              onClick={() => handleGeneralAdmissionQuantityChange(seatData, -1)}
                              title="Decrease quantity"
                              width="28px"
                              height="28px"
                              disabled={isUpdatingQuantity}
                            >
                              −
                            </Button>
                            <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '14px' }}>{quantity}</span>
                            <Button
                              onClick={() => handleGeneralAdmissionQuantityChange(seatData, 1)}
                              title="Increase quantity"
                              width="28px"
                              height="28px"
                              disabled={isUpdatingQuantity}
                            >
                              +
                            </Button>
                          </div>
                        ) : (
                          // Show × button when quantity = 1 (with confirmation modal)
                          <TicketSummaryRemoveButton
                            onClick={() => handleRemoveClick({
                              isGeneralAdmission: true,
                              blockId: seatData.blockId,
                              ticketTypeId: seatData.ticketTypeId
                            })}
                            title="Remove tickets"
                          >
                            ×
                          </TicketSummaryRemoveButton>
                        )}
                      </div>
                    </TicketSummaryItem>
                  );
                }
                
                // Handle regular seat tickets
                // Format seat display: block name + seat row + seat number (e.g., "Block A10" or "A10")
                let seatDisplay;
                if (seatData.seatRow || seatData.seatNumber) {
                  // Format: "A10" (row + number) or "Block A10" if block name available
                  const seatLabel = `${seatData.seatRow}${seatData.seatNumber}`;
                  seatDisplay = seatData.blockName 
                    ? `${seatData.blockName} - Seat ${seatLabel}`
                    : seatLabel;
                } else if (seatData.blockName) {
                  // Fallback: block name + seat ID
                  seatDisplay = `${seatData.blockName} - Seat ${seatId}`;
                } else {
                  // Last resort: just seat ID
                  seatDisplay = `Seat ${seatId}`;
                }
                
                return (
                  <TicketSummaryItem key={seatId}>
                    <TicketSummaryInfo>
                      <TicketSummarySeatId>{seatDisplay}</TicketSummarySeatId>
                      <TicketSummaryType>{seatData.ticketTypeTitle || ticketType?.title || 'Ticket'}</TicketSummaryType>
                    </TicketSummaryInfo>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {renderRowPriceDisplay(seatData, ticketType, false)}
                      <TicketSummaryRemoveButton
                        onClick={() => handleRemoveClick({
                          seatId: seatId,
                          blockId: seatData.blockId,
                          ticketTypeId: seatData.ticketTypeId
                        })}
                        title="Remove seat"
                      >
                        ×
                      </TicketSummaryRemoveButton>
                    </div>
                  </TicketSummaryItem>
                );
              })}
            </TicketSummaryList>
          )}
          {hasOrderLines && (() => {
            let total = selectedPackageList.reduce((sum, pkg) => sum + (Number(pkg.totalPrice) || 0), 0);
            individualSeatEntries.forEach(([ , seatData]) => {
              const ticketType = ticketTypes.find(tt => tt.ticket_type_id === seatData.ticketTypeId);
              if (ticketType) {
                const unitPrice = seatData.coupon_applied ? seatData.coupon_applied_price : (ticketType.total_price || ticketType.price || 0);
                const quantity = seatData.isGeneralAdmission ? (seatData.quantity || 1) : 1;
                total += unitPrice * quantity;
              }
            });
            
            return (
              <TicketSummaryTotal>
                <TicketSummaryTotalLabel>Total</TicketSummaryTotalLabel>
                <TicketSummaryTotalAmount>R {total.toFixed(2)}</TicketSummaryTotalAmount>
              </TicketSummaryTotal>
            );
          })()}
          <TicketSummaryButtons>
            <Button
              text={isLoadingSeatingData ? 'Loading...' : (selectedPackageList.length > 0 ? 'Add more seats' : 'Select Seats')}
              onClick={() => openSeatSelector(null)}
              disabled={isLoadingSeatingData}
              width="100%"
              backgroundColor={'#FFFFFF'}
              textColor={Theme.Text.Color || '#3498db'}
              border={'1px solid ' + (Theme.Text.Color || '#3498db')}
              fontFamily={FontFamilies.Bold}
            />
            {hasOrderLines && (
              <Button
                text="Pay Now"
                onClick={handlePayNow}
                disabled={!packageSlotsFilled || !packageVariantsSelected}
                width="100%"
                backgroundColor={Colors.PrimaryOne || '#3498db'}
                textColor="#ffffff"
              />
            )}
          </TicketSummaryButtons>
          {selectedPackageList.length > 0 && (!packageSlotsFilled || !packageVariantsSelected) && (
            <Text color={"#e74c3c"} fontSize={FontSizes.Small} padding={'12px 0 0 0'} textAlign={'center'}>
              {!packageVariantsSelected
                ? 'Select a size for each item in your package to enable the "Pay Now" button.'
                : 'Select a seat for each ticket in your package to enable the "Pay Now" button.'}
            </Text>
          )}
        </TicketSummaryContainer>

        {isSeatSelectorOpen && renderSeatingMap()}

      </FullWidthContainer>

      <Modal backgroundColor={'#FFFFFF'} hideCloseButton show={showTimeWarningModal} onClose={() => setShowTimeWarningModal(false)}>
        <ModalContent>
          <ModalTitle>Limited Time Remaining</ModalTitle>
          <ModalMessage>
            You have less than <span style={{ fontFamily: FontFamilies.Bold }}>2 minutes</span> left to select your seats. Please complete your selection soon to avoid losing your reserved seats.
          </ModalMessage>
          <Button onClick={() => setShowTimeWarningModal(false)}>Continue</Button>
        </ModalContent>
      </Modal>

      <Modal
        backgroundColor={Theme.Card.Background}
        show={showRemoveConfirmModal}
        hideCloseButton={true}
        onClose={handleCancelRemove}
        className="modal"
      >
        <ModalContent>
          <ModalTitle>Remove Ticket?</ModalTitle>
          <ModalMessage>
            Are you sure you want to remove this ticket from your selection? This action cannot be undone.
          </ModalMessage>
          <ModalButtons>
            <Button
              onClick={handleCancelRemove}
              backgroundColor={Theme.Card.Background}
              textColor={Theme.Text.Color}
              border={`1px solid ${Theme.Text.Color}`}
              width="100%"
              padding="10px 20px"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemove}
              backgroundColor={Colors.PrimaryOne || '#3498db'}
              textColor="#ffffff"
              width="100%"
              padding="10px 20px"
            >
              Remove
            </Button>
          </ModalButtons>
        </ModalContent>
      </Modal>

      <Modal
        backgroundColor={Theme.Card.Background}
        show={showRemovePackageModal}
        hideCloseButton={true}
        onClose={handleCancelRemovePackage}
        className="modal"
      >
        <ModalContent>
          <ModalTitle>Remove Package?</ModalTitle>
          <ModalMessage>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><div>Are you sure you want to remove this package?</div><div style={{ color: '#e74c3c', fontFamily: FontFamilies.Bold }}>Seats selected for it will be released.</div></div>
          </ModalMessage>
          <ModalButtons>
            <Button
              onClick={handleCancelRemovePackage}
              backgroundColor={Theme.Card.Background}
              textColor={Theme.Text.Color}
              border={`1px solid ${Theme.Text.Color}`}
              width="100%"
              padding="10px 20px"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemovePackage}
              backgroundColor={Colors.PrimaryOne || '#3498db'}
              textColor="#ffffff"
              width="100%"
              padding="10px 20px"
            >
              Remove
            </Button>
          </ModalButtons>
        </ModalContent>
      </Modal>
    </>
  );
};

export const EventSeating = withCommon(EventSeatingUI, {
  showLoadingOnLoad: true
});